import { Card, Table, Tag, notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdRefresh, MdReplay } from "react-icons/md";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";

const STATUS_COLORS = {
  SUCCESS: "green",
  FAILURE: "red",
  SHORT_CIRCUITED: "orange",
  SKIPPED: "default",
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "");

const View = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.CONNECTOR_LOGS);
  const COLUMNS = t("COLUMNS", { returnObjects: true });

  const { data, loading, refetch } = useQuery(graphql.queries.CONNECTOR_CALLS, {
    variables: { take: 200, orderBy: [{ createdDate: "DESC" }] },
    fetchPolicy: "network-only",
  });
  const [replayConnectorCall, { loading: replaying }] = useMutation(
    graphql.mutations.REPLAY_CONNECTOR_CALL,
  );

  const [replayingId, setReplayingId] = useState(null);

  const calls = get(data, "connectorCalls.data") || [];

  const replay = async (id) => {
    setReplayingId(id);
    try {
      await replayConnectorCall({ variables: { id } });
      notification.success({ message: t("REPLAYED") });
      refetch();
    } catch (_) {
      notification.error({ message: t("REPLAY_ERROR") });
    } finally {
      setReplayingId(null);
    }
  };

  const columns = [
    {
      title: COLUMNS.CONNECTOR,
      dataIndex: "connectorKey",
      render: (connectorKey, row) => (
        <div>
          <div className="connector-logs__name">{connectorKey}</div>
          <div className="connector-logs__meta">
            {row.category}
            {row.replay ? (
              <Tag className="connector-logs__replay-tag">{t("REPLAY")}</Tag>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: COLUMNS.STATUS,
      dataIndex: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: COLUMNS.DURATION,
      dataIndex: "durationMs",
      render: (durationMs) => (durationMs == null ? "—" : `${durationMs} ms`),
    },
    {
      title: COLUMNS.DATE,
      dataIndex: "createdDate",
      render: formatDate,
    },
    {
      title: COLUMNS.ACTIONS,
      key: "actions",
      render: (_value, row) => (
        <Button
          type="primary--outlined"
          onClick={() => replay(row.id)}
          disabled={replaying && replayingId === row.id}
        >
          <MdReplay size={16} /> {t("REPLAY")}
        </Button>
      ),
    },
  ];

  const expanded = (row) => (
    <div className="connector-logs__detail-grid">
      <div className="connector-logs__meta">
        {t("RUN")}: {row.runId || "—"} · {t("STEP")}: {row.stepId || "—"}
      </div>
      <div>
        <div className="connector-logs__detail-title">{t("REQUEST")}</div>
        <pre className="connector-logs__detail-pre">
          {JSON.stringify(row.request, null, 2)}
        </pre>
      </div>
      <div>
        <div className="connector-logs__detail-title">{t("RESPONSE")}</div>
        <pre className="connector-logs__detail-pre">
          {JSON.stringify(row.error || row.response, null, 2)}
        </pre>
      </div>
    </div>
  );

  const actions = [
    {
      icon: <MdRefresh size={20} />,
      text: t("REFRESH"),
      onClick: () => refetch(),
      type: "primary--outlined",
    },
  ];

  return (
    <>
      <Header title={t("TITLE")} subtitle={t("SUBTITLE")} actions={actions} />
      <Card type="simple">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={calls}
          columns={columns}
          expandable={{ expandedRowRender: expanded }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
        />
      </Card>
    </>
  );
};

export default View;
